//src/components/ExportXlsxButton.tsx
import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Popover, Space, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { exportToExcelAdvanced } from '../utils/excel';
import type { ExcelColumn } from '../utils/excel';

const { RangePicker } = DatePicker;
const DAY_MS = 24 * 60 * 60 * 1000;

export type ExportXlsxButtonProps<T> = {
  source: T[];
  filename: string;
  sheetName?: string | undefined;
  columns: ReadonlyArray<ExcelColumn>;
  map: (item: T) => unknown | unknown[];
  getTime: (item: T) => number | null | undefined;
  title?: string | undefined;
  className?: string | undefined;
  buttonText?: string | undefined;
};

export default function ExportXlsxButton<T>({
  source,
  filename,
  sheetName = 'Sheet1',
  columns,
  map,
  getTime,
  title = 'Экспорт в Excel',
  className,
  buttonText = 'Экспорт в Excel',
}: ExportXlsxButtonProps<T>) {
  const btnClass = ['header-action-btn', className].filter(Boolean).join(' ');
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [loading, setLoading] = useState(false);

  const presets = useMemo(() => {
    const now = Date.now();
    const to = new Date(now);
    return [
      { label: '24 ч',      from: new Date(now - DAY_MS),        to },
      { label: 'Неделя',    from: new Date(now - 7 * DAY_MS),    to },
      { label: 'Месяц',     from: new Date(now - 30 * DAY_MS),   to },
      { label: 'Год',       from: new Date(now - 365 * DAY_MS),  to },
      { label: 'Всё время', from: null,                           to: null },
    ] as const;
  }, []);

  async function onExport() {
    if (!source.length) {
      message.info('Нет данных для экспорта');
      return;
    }

    try {
      setLoading(true);
      const [from, to] = range;

      // Фильтруем по диапазону дат
      const filtered = source.filter((item) => {
        if (!from && !to) return true;
        const t = getTime(item);
        if (t == null || !Number.isFinite(t)) return false;
        if (from && t < from.getTime()) return false;
        if (to && t > to.getTime()) return false;
        return true;
      });

      if (!filtered.length) {
        message.info('Нет данных для экспорта в выбранном диапазоне');
        return;
      }

      await exportToExcelAdvanced({
        filename,
        sheetName,
        data: filtered,
        map,
        columns,
        range: null, // уже отфильтровано здесь
      });

      setOpen(false);
    } finally {
      setLoading(false);
    }
  }

  const content = (
    <div style={{ width: 320 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <RangePicker
          style={{ width: '100%' }}
          onChange={(vals) =>
            setRange([vals?.[0]?.toDate() ?? null, vals?.[1]?.toDate() ?? null])
          }
        />
        <div style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
          {presets.map((p) => (
            <Button key={p.label} size="small" onClick={() => setRange([p.from, p.to])}>
              {p.label}
            </Button>
          ))}
        </div>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={loading}
            onClick={onExport}
          >
            Скачать XLSX
          </Button>
        </Space>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      title={title}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <Button className={btnClass} icon={<DownloadOutlined />} disabled={loading}>
        {buttonText}
      </Button>
    </Popover>
  );
}
